import { ExclamationCircleOutlined } from "@ant-design/icons";
import {
	Button,
	Checkbox,
	Divider,
	Empty,
	Flex,
	Form,
	Input,
	Modal,
	Popconfirm,
	Space,
	Table,
	Tag,
	Tooltip,
	Upload,
} from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import elementalSvg from "../assets/image/elemental 'e'.svg";
import CustomForm from "../components/CustomForm";
import FormButtonSave from "../components/FormButtonSave";
import AuditLogModal from "../components/Modal/AuditLogModal";
import DeliverablesDiscussionModal from "../components/Modal/DeliverablesDiscussionModal";
import SearchButton from "../components/SearchButton";
import useContainerDimensions from "../hooks/useContainerDimensions";
import usePermission from "../hooks/usePermission";
import {
	getDeliverables,
	updateDeliverableItem,
} from "../services/api-server/deliverables";
import {
	fileDownload,
	fileRemove,
	fileUpload,
} from "../services/api-server/files";
import { getOperations } from "../services/api-server/operations";
import { getPhaseItem } from "../services/api-server/phases";
import { getProject } from "../services/api-server/projects";
import { getTask } from "../services/api-server/tasks";
import { getAllUsers } from "../services/api-server/user";
import { GetAntIcon } from "../utils/ant_icons";
import Emitter from "../utils/emitter";
import { getCrudaClass } from "../utils/lookup_list";
import { socket } from "../utils/socket";
import { excludedMark, statusIcon } from "../utils/statusIcon";
import { checkPrivilege, checkProjectTeam, emailsEqual } from "../utils/utils";
import { HomeContext } from "./Home";
import NoAccess from "./NoAccess";

import DeliverablesSharepointComponent from "../components/DeliverablesSharepointComponent";
import TaskSettingsButton from "../components/TaskSettingsButton";
import AuditLogButton from "../components/AuditLogButton";

function TaskOverview(props: any) {
	const context: any = useContext(HomeContext);
	const location = useLocation();
	const navigate = useNavigate();

	const [projectTitle, setProjectTitle] = useState<any>(null);
	const [project, setProject] = useState<any>(null);
	const [wellTitle, setWellTitle] = useState<any>(null);
	const [phaseTitle, setPhaseTitle] = useState<any>(null);
	const [taskInfo, setTaskInfo] = useState<any>(null);
	const [taskDetailsExpand, setTaskDetailsExpand] = useState(false);
	const [activePage, setActivePage] = useState<any>("deliverables");
	const [deliverableList, setDeliverableList] = useState<any>([]);
	const [filteredDeliverableList, setFilteredDeliverableList] =
		useState<any>(null);
	const [sideContainerOpen, setSideContainerOpen] = useState<any>(false);
	const [sideContainerData, setSideContainerData] = useState<any>(null);
	const [deliverableKey, setDeliverableKey] = useState<any>(null);
	const [tempRecord, setTempRecord] = useState<any>(null);
	const [searchRecord, setSearchRecord] = useState<any>([]);
	const [project_code, setProjectCode] = useState<any>(null);
	const [operationCode, setOperationCode] = useState<any>(null);
	const [phase_id, setPhaseId] = useState<any>(null);
	const [task_id, setTaskId] = useState<any>(null);
	const [responsibleParty, setResponsibleParty] = useState<any>([]);
	const [reopenModal, setReopenModal] = useState<any>(false);
	const [cancelPopover, setCancelPopover] = useState<any>(false);
	const [formReset, setFormReset] = useState<boolean>(true);

	const [tableRef, tableDimensions] = useContainerDimensions();

	const [allUsers, setAllUsers] = useState<any>(null);

	const hasOperationalUpdatePermission = usePermission(
		"Operational Update.Operation",
		project_code
	);

	interface User {
		_id: string;
		name: string;
		email: string;
		theme: string;
		__v: number;
		country_code: string;
		phone_number: string;
		profile_picture: string;
	}

	function ChecklistSection({
		data,
		updateData,
	}: {
		data: any;
		updateData: (data: any) => void;
	}) {
		// Initialize checked state from the data
		const [checklist, setChecklist] = useState(data.checklist);
		const [checkedCount, setCheckedCount] = useState(0);

		useEffect(() => {
			setCheckedCount(checklist.filter((item: any) => item.checked).length);
		}, [checklist]);

		const handleCheckChange = (itemId: string) => {
			const updatedChecklist = checklist.map((item: any) => {
				if (item._id === itemId) {
					console.log(item);
					return {
						...item,
						checked: !item.checked,
					};
				}
				return item;
			});
			setChecklist(updatedChecklist);

			if (data.status === "New") {
				updateData({ status: "In progress", checklist: updatedChecklist });
			} else {
				updateData({ checklist: updatedChecklist });
			}
		};

		return (
			<>
				<div style={{ paddingBottom: "24px" }}>
					<Divider orientation="left" style={{ color: "grey" }}>
						<Space>
							{GetAntIcon("checkcircle")}
							<span>
								Checklist ({checkedCount}/{checklist.length})
							</span>
						</Space>
					</Divider>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "8px",
						}}
					>
						{checklist.map((item: any) => (
							<Checkbox
								key={item._id}
								checked={item.checked}
								onChange={() => handleCheckChange(item._id)}
								disabled={["In review", "Resolved", "Closed"].includes(
									data.status
								)}
							>
								{item.name}
							</Checkbox>
						))}
					</div>
				</div>
			</>
		);
	}

	const commentingUser = (email: any) => {
		// Find if there is a user with a name in the MongoDB document by comparing this current active user to users within the document
		const commentingUser = allUsers?.filter((user: User) =>
			emailsEqual(user.email, email)
		);
		// If a document(JSON object) regarding the user is found and if the document is not empty
		if (commentingUser && commentingUser.length > 0) {
			// Return the document as an (JSON object)
			return commentingUser[0];
		} else {
			return null;
		}
	};

	useEffect(() => {
		const _responsibleParty = project?.role_assignments.filter((role: any) =>
			taskInfo.responsible_roles.includes(role.role_id)
		);
		setResponsibleParty(_responsibleParty);
	}, [taskInfo]);

	// 	Audit Logs states
	const [auditOpen, setAuditOpen] = useState<any>(false);
	const [auditDeliverableOpen, setAuditDeliverableOpen] = useState<any>(false);
	const [auditData, setAuditData] = useState<any>([]);

	const [form] = Form.useForm();
	const [reopenForm] = Form.useForm();

	const getRoleNamesFromProject = (data: any, key: any) => {
		if (!data || !Array.isArray(data[key])) return [];
		const roleAssignments = project?.role_assignments || [];
		return data[key]
			.map((roleId: any) => {
				if (roleId === "project_manager") return "Project manager";
				const found = roleAssignments.find(
					(role: any) => role.role_id === roleId
				);
				return found?.role || null;
			})
			.filter((name: any) => name !== null);
	};

	const renderRoleTags = (names: any) => {
		if (names?.length > 0) {
			return names.map((role: any) => (
				<Tag className="deliverable-tag" key={role}>
					{role}
				</Tag>
			));
		}
		return <span>-</span>;
	};

	const mapRolesData = (data: any, extraData: any) => {
		let role_assignments_data =
			extraData?.roles?.map((element: any) => {
				return { label: element.name, value: element._id };
			}) || [];
		role_assignments_data.push({
			label: "Project manager",
			value: "project_manager",
		});

		return (
			role_assignments_data
				.filter((roles: any) => data.includes(roles.value))
				?.map((obj: any) => obj.label) || []
		);
	};

	const loadData = (
		project_code: string,
		operation_code: string,
		phase_id: string,
		task_id: string,
		deliverable_id: string,
		extraData: any
	) => {
		Emitter.emit("loading", { name: "task-overview", isLoading: true });
		console.log("debug 1 ", operation_code);
		Promise.all([
			getProject(context.currentTenantKey, project_code),
			getOperations(context.currentTenantKey, {
				project_code,
				operation_code,
			}),
			getPhaseItem(context.currentTenantKey, phase_id),
			getTask(context.currentTenantKey, task_id),
			getDeliverables(context.currentTenantKey, task_id),
			getAllUsers(),
		])
			.then((values: any) => {
				setProjectTitle(values[0]?.project_name);
				context.setCurrentProject(values[0]);
				if (values[1] && values[1].length > 0) {
					setWellTitle(values[1][0]?.well_project_name);
					context.setCurrentOperation(values[1][0]);
				}
				setPhaseTitle(values[2][0]?.phase_name);
				context.setCurrentPhase(values[2][0]);
				setTaskInfo(values[3]);
				context.setCurrentTask(values[3]);

				// from values[4] which is the deliverable data, create a new field which is responsible_roles_names, accountable_roles_names, consulted_names, informed_parties_names based on the id of the respective roles field already exists
				values[4]
					//TO BE REMOVED
					?.sort((a: any, b: any) => a.created_on.localeCompare(b.created_on))
					.map((deliverable: any) => {
						deliverable.assigned_to_names = mapRolesData(
							deliverable.assigned_to,
							extraData
						);
						deliverable.reviewed_by_names = mapRolesData(
							deliverable.reviewed_by,
							extraData
						);
					});

				setAllUsers(values[5]);
				console.log(values[5]);

				setDeliverableList(values[4]);
				Emitter.emit("loading", { name: "task-overview", isLoading: false });

				if (sideContainerData?._id) {
					const newSideContainerData = values[4].find((item: any) => {
						return item._id === sideContainerData._id;
					});
					//this thing will re-render the useEffect
					setSideContainerData(newSideContainerData);
					setSideContainerOpen(true);
				} else if (deliverable_id) {
					const newSideContainerData = values[4].find((item: any) => {
						return item._id === deliverable_id;
					});
					//this thing will re-render the useEffect
					setSideContainerData(newSideContainerData);
					setSideContainerOpen(true);
				}
			})
			.catch((e: any) => {
				console.log(e);
				// setIsLoading(false);
				Emitter.emit("loading", { name: "task-overview", isLoading: false });
			})
			.finally(() => {
				// calculateTableHeight();
				Emitter.emit("loading", { name: "task-overview", isLoading: false });
			});
	};

	useEffect(() => {
		socket.on("REFRESH_DATA", () => {
			loadData(
				project_code,
				operationCode,
				phase_id,
				task_id,
				deliverableKey,
				context.allData
			);
			Emitter.emit("alert", {
				type: "success",
				message: `Data refreshed based on latest update.`,
				description: "",
				top: true,
				closeable: false,
				timeout: 3000,
			});
		});
		return () => {
			socket.removeListener("REFRESH_DATA", () =>
				loadData(
					project_code,
					operationCode,
					phase_id,
					task_id,
					deliverableKey,
					context.allData
				)
			);
		};
	}, [
		sideContainerData,
		project_code,
		operationCode,
		phase_id,
		task_id,
		deliverableKey,
	]);

	useEffect(() => {
		Emitter.emit("loading", { name: "task-overview", isLoading: true });
		if (context.currentTenantKey && context.allData) {
			let project_code: any =
				location?.state?.project_code || location?.pathname?.split("/")[2];
			let operation_code: any =
				location?.state?.operation_code || location?.pathname?.split("/")[3];
			let phase_id: any =
				location?.state?.phase_id || location?.pathname?.split("/")[4];
			let task_id: any =
				location.state?.task_id || location?.pathname?.split("/")[5];

			let deliverable_id: any = location.state?.deliverable_id;
			let foundProject = context.allData?.projects?.find(
				(project: any) => project?.project_code === project_code
			);

			setProjectCode(project_code);
			setOperationCode(operation_code);
			setDeliverableKey(deliverable_id);
			setPhaseId(phase_id);
			setTaskId(task_id);

			// Have to handle archive
			if (foundProject) {
				setProjectTitle(foundProject.project_name);
				setProject(foundProject);
			}
			loadData(
				project_code,
				operationCode,
				phase_id,
				task_id,
				deliverable_id,
				context.allData
			);
		}
	}, [context.currentTenantKey, context.allData]);

	// Searching records
	useEffect(() => {
		if (searchRecord && tempRecord) {
			let recordIndex = searchRecord.findIndex((item: any) => {
				return item.columnKey === tempRecord.columnKey;
			});
			if (recordIndex !== -1) {
				if (tempRecord.value != "") {
					let _record = [...searchRecord];
					_record[recordIndex] = tempRecord;
					setSearchRecord(_record);
				} else {
					let _record = [...searchRecord];
					_record.splice(recordIndex, 1);
					setSearchRecord(_record);
				}
			} else {
				if (tempRecord.value != "") {
					let _record = [...searchRecord];
					_record.push(tempRecord);
					setSearchRecord(_record);
				}
			}
		}
	}, [tempRecord]);

	// Filter data and table columns based on search record
	useEffect(() => {
		let data: any = Array.isArray(deliverableList) ? [...deliverableList] : [];
		if (searchRecord?.length > 0) {
			let filteredData = data
				?.map((element: any) => {
					let bool = searchRecord.every((item: any) => {
						if (Array.isArray(element[item.columnKey])) {
							return element[item.columnKey].some((arrayItem: any) =>
								arrayItem.toLowerCase().includes(item.value.toLowerCase())
							);
						} else {
							return element[item.columnKey]
								?.toLowerCase()
								.includes(item.value.toLowerCase());
						}
					});
					if (bool) return element;
				})
				.filter((element: any) => element !== null && element !== undefined);
			setFilteredDeliverableList(filteredData);
			data = filteredData;
		} else {
			setFilteredDeliverableList(null);
		}
	}, [searchRecord]);

	const setTaskPanelContent = (taskInfo: any, key: any) => {
		let content = "-";
		// if key exists in taskInfo update content
		if (taskInfo && taskInfo[key]) {
			if (
				[
					"responsible_roles",
					"accountable_roles",
					"consulted",
					"informed_parties",
				].includes(key)
			) {
				const names = getRoleNamesFromProject(taskInfo, key);
				content = renderRoleTags(names);
			} else {
				content = taskInfo[key];
			}
		}
		return content;
	};

	const plannedStartDate = setTaskPanelContent(taskInfo, "planned_start_date");
	const formattedPlannedStartDate = plannedStartDate
		? dayjs(plannedStartDate).isValid()
			? dayjs(plannedStartDate).format("DD MMMM YYYY")
			: "-"
		: null;

	const renderMetaData = () => {
		return (
			<>
				{/* Task details container */}
				<div
					style={{
						display: "flex",
						// flexWrap: "wrap",
						gap: "20px",
						rowGap: "20px",
					}}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "20px",
							minWidth: "30%",
						}}
					>
						{/* Task title */}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								width: "100%",
							}}
						>
							<div className="panel-detail-label">Task title</div>
							<div className="panel-item-content">
								{setTaskPanelContent(taskInfo, "task_name")}
							</div>
						</div>

						{/* Status */}
						<div
							style={{ display: "flex", flexDirection: "column", width: "30%" }}
						>
							<div className="panel-detail-label">Status</div>
							<div className="panel-item-content">
								<>
									{taskInfo?.closed ? (
										<>Closed</>
									) : (
										<>
											{setTaskPanelContent(taskInfo, "status") !== "Late" ? (
												setTaskPanelContent(taskInfo, "status")
											) : (
												<div style={{ display: "flex", gap: "3px" }}>
													{setTaskPanelContent(taskInfo, "status")}{" "}
													<Tooltip
														title={
															<Flex vertical gap={20}>
																<Flex vertical>
																	<div>
																		<b>Reason:</b>
																	</div>
																	<div>
																		{setTaskPanelContent(taskInfo, "blocker")}
																	</div>
																</Flex>
																<Flex vertical>
																	<div>
																		<b>Plan:</b>
																	</div>
																	<div>
																		{setTaskPanelContent(taskInfo, "plan")}
																	</div>
																</Flex>
															</Flex>
														}
														placement="right"
													>
														<div style={{ color: "rgba(255,255,255,0.45)" }}>
															{GetAntIcon("question3")}
														</div>
													</Tooltip>
												</div>
											)}
										</>
									)}
								</>
							</div>
						</div>

						{/* Objective */}
						<div style={{ display: "flex", flexDirection: "column" }}>
							<div className="panel-detail-label">Objective</div>
							<div className="panel-item-content">
								{setTaskPanelContent(taskInfo, "objective")}
							</div>
						</div>
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "20px",
							minWidth: "70%",
						}}
					>
						{/* Planned start date */}
						<div style={{ display: "flex", flexDirection: "column" }}>
							<div className="panel-detail-label">Planned start date</div>
							<div className="panel-item-content">
								{formattedPlannedStartDate}
							</div>
						</div>
						{/* Est. duration (days) */}
						<div style={{ display: "flex", flexDirection: "column" }}>
							<div className="panel-detail-label">Est. duration (days)</div>
							<div className="panel-item-content">
								{setTaskPanelContent(taskInfo, "estimated_duration_days")}
							</div>
						</div>
						{/* Notes */}
						<div style={{ display: "flex", flexDirection: "column" }}>
							<div className="panel-detail-label">Notes</div>
							<div className="panel-item-content">
								{setTaskPanelContent(taskInfo, "notes")}
							</div>
						</div>
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								columnGap: "35px",
								rowGap: "20px",
							}}
						>
							{/* Responsible */}
							<div style={{ display: "flex", flexDirection: "column" }}>
								<div className="panel-detail-label">Responsible</div>
								<div className="panel-item-content">
									{setTaskPanelContent(taskInfo, "responsible_roles")}
								</div>
							</div>

							{/* Accountable */}
							<div style={{ display: "flex", flexDirection: "column" }}>
								<div className="panel-detail-label">Accountable</div>
								<div className="panel-item-content">
									{setTaskPanelContent(taskInfo, "accountable_roles")}
								</div>
							</div>

							{/* Consulted */}
							<div style={{ display: "flex", flexDirection: "column" }}>
								<div className="panel-detail-label">Consulted</div>
								<div className="panel-item-content">
									{setTaskPanelContent(taskInfo, "consulted")}
								</div>
							</div>

							{/* Informed */}
							<div style={{ display: "flex", flexDirection: "column" }}>
								<div className="panel-detail-label">Informed</div>
								<div className="panel-item-content">
									{setTaskPanelContent(taskInfo, "informed_parties")}
								</div>
							</div>
						</div>
					</div>
				</div>
			</>
		);
	};

	// Tab Buttons
	const buttonTopic = (item: any, iconType: string) => {
		return (
			<div
				className={activePage === item.key ? "project-active-button" : ""}
				style={{
					display: "flex",
					padding: "4px 15px",
					gap: "8px",
					cursor: "pointer",
				}}
				onClick={() => {
					setActivePage(item.key);
				}}
			>
				{/* Pass the `iconType` to get different icons based on different tab buttons */}
				{GetAntIcon(iconType)}
				<span>{item.label}</span>
			</div>
		);
	};

	const updateDeliverableAndAlertMessage = (
		deliverable_id: any,
		newData: any,
		initialData: any
	) => {
		updateDeliverableItem(
			context.currentTenantKey,
			deliverable_id,
			{ ...newData, project_code, operationCode, task_id },
			initialData,
			props.params.user,
			["status", "checklist"]
		)
			.then((updatedData: any) => {
				const updatedDeliverableList = deliverableList?.map((item: any) =>
					item._id === updatedData._id ? updatedData : item
				);
				setDeliverableList(updatedDeliverableList);
				setSideContainerData(updatedData);

				const taskData: any = taskInfo;
				taskData.project_code = project?.project_code;
				taskData.operation_code = context?.currentOperation?.operation_code;
				const allDeliverablesStatus = updatedDeliverableList.map(
					(item: any) => item.status
				);

				if (
					allDeliverablesStatus.every((status: any) => status === "Resolved")
				) {
					taskData.status = "Resolved";
				} else if (
					allDeliverablesStatus.every((status: any) => status === "New")
				) {
					taskData.status = "New";
				} else {
					taskData.status = "In progress";
				}

				// updateTaskItem(
				// 	context.currentTenantKey,
				// 	taskInfo._id,
				// 	{
				// 		...taskData,
				// 		project_code: project_code,
				// 		operation_code: operation_code,
				// 	},
				// 	null,
				// 	props.params.user
				// ).then((updatedTaskData: any) => {
				// 	console.log(project?.project_code);
				// 	setTaskInfo(updatedTaskData);
				// 	socket.emit("UPDATING_DATA");
				// });

				let alertMessage: any = null;
				switch (newData?.status) {
					case "In progress":
						alertMessage = "Deliverable status updated to 'In progress'.";
						break;
					case "In review":
						alertMessage = "Deliverable submitted for review.";
						break;
					case "Resolved":
						alertMessage = "Deliverable marked as 'Resolved'.";
						break;
				}

				if (alertMessage) {
					Emitter.emit("alert", {
						type: "success",
						message: alertMessage,
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				}

				form.resetFields();
			})
			.catch((error: any) => {
				console.log(error);
			});
	};

	const handleSearch = (record: any) => {
		setTempRecord(record);
	};

	const renderDeliverables = () => {
		const deliverablesColumn: any = [
			{
				title: (
					<SearchButton
						handleSearch={handleSearch}
						headerName="Deliverables"
						columnKey="deliverable_name"
						key="deliverables"
					/>
				),
				dataIndex: "deliverable_name",
				key: "deliverable_name",
				width: 400,
				render: (text: any, record: any) => {
					if (record.isEmpty) {
						return null;
					}
					const tooltipText = record?.disabled &&
						record?.disabled_reason != "" && (
							<span>
								This deliverable is excluded from the well programme. You may
								still click to view the details. <br />
								<br /> <b>Comment:</b>
								<br />
								{record?.disabled_reason}
							</span>
						);
					return (
						<Tooltip title={tooltipText}>
							<div
								style={{
									cursor: "pointer",
									display: "flex",
									gap: "10px",
									filter: record.disabled ? "grayscale(1)" : "none",
								}}
								onClick={() => {
									// Reset the state by navigating to the same path without state
									navigate(location.pathname, {
										replace: true,
										state: {
											deliverable_id: record._id,
											task_id: task_id,
										},
									});

									setSideContainerOpen(true);
									setDeliverableKey(record._id);
									setSideContainerData(record);
								}}
							>
								{record.disabled && excludedMark()}
								{statusIcon(record.status)}
								<span className="table-clickable-text">{text}</span>
							</div>
						</Tooltip>
					);
				},
			},
			{
				title: (
					<SearchButton
						handleSearch={handleSearch}
						headerName="Prepared by"
						columnKey="assigned_to_names"
						key="assigned_to_names"
					/>
				),
				dataIndex: "assigned_to_names",
				key: "assigned_to_names",
				width: 300,
				render: (_: any, record: any) => {
					if (record.isEmpty) {
						return null;
					}
					return renderRoleTags(getRoleNamesFromProject(record, "assigned_to"));
				},
			},
			{
				title: (
					<SearchButton
						handleSearch={handleSearch}
						headerName="Accepted by"
						columnKey="reviewed_by_names"
						key="reviewed_by_names"
					/>
				),
				dataIndex: "reviewed_by_names",
				key: "reviewed_by_names",
				width: 300,
				render: (_: any, record: any) => {
					if (record.isEmpty) {
						return null;
					}
					return renderRoleTags(getRoleNamesFromProject(record, "reviewed_by"));
				},
			},
		];

		const handleFileDownload = (file: any) => {
			let azureFilePath = `${context.currentProject.project_code}/${context.currentOperation.operation_code}/${context.currentPhase._id}/${context.currentTask._id}/${deliverableKey}/${file.actual_name}`;
			let tenant = context.currentTenantKey;
			fileDownload(file, azureFilePath, tenant, true)
				.then(() => {
					Emitter.emit("alert", {
						type: "success",
						message: "File succesfully downloaded.",
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				})
				.catch((error: any) => {
					Emitter.emit("alert", {
						type: "error",
						message: "File not found on server. Please contact administrator.",
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				});
		};

		/* Deliverables Side Container */
		const sideContainer = () => {
			return (
				<div className="deliverable-side-container">
					<div
						style={{ position: "relative", overflow: "auto", padding: "10px" }}
					>
						<Button
							type="link"
							className="deliverable-card-close-button"
							onClick={() => {
								navigate(location.pathname, {
									replace: true,
									state: null,
								});
								setSideContainerOpen(false);
							}}
						>
							{GetAntIcon("close")}
						</Button>
						<div>
							{sideContainerData && (
								<Form
									form={form}
									className={`deliverable-card-form ${
										sideContainerData.status === "Resolved"
											? "padding-resolved"
											: "padding-not-resolved"
									}`}
									onFinish={(values: any) => {
										updateDeliverableAndAlertMessage(
											sideContainerData._id,
											values,
											sideContainerData
										);
									}}
								>
									{sideContainerData.disabled &&
										sideContainerData.disabled_reason && (
											<span className="disabled-reason">
												**Excluded from well plan. Comment:{" "}
												{sideContainerData.disabled_reason}
											</span>
										)}
									<div className="deliverable-card-form-item">
										<span className="deliverable-card-form-label">
											Deliverable title
										</span>
										<span
											style={{
												fontSize: "14px",
												textAlign: "left",
												height: "min-content",
											}}
										>
											{sideContainerData?.deliverable_name}
										</span>
									</div>
									<div className="deliverable-card-form-item">
										<span className="deliverable-card-form-label">Status</span>
										<Form.Item
											name={"status"}
											style={{ padding: 0, margin: 0 }}
										>
											<span className="form-item-content">
												{sideContainerData?.status}
											</span>
										</Form.Item>
									</div>
									<div
										style={{
											display: "flex",
											gap: "20px",
											// flexGrow: 1,
											flexWrap: "wrap",
										}}
									>
										<div className="deliverable-card-form-item">
											<span className="deliverable-card-form-label">
												Prepared by
											</span>
											<span className="form-item-content">
												{renderRoleTags(
													getRoleNamesFromProject(
														sideContainerData,
														"assigned_to"
													)
												)}
											</span>
										</div>
										<div className="deliverable-card-form-item">
											<span className="deliverable-card-form-label">
												Accepted by
											</span>
											<span className="form-item-content">
												{renderRoleTags(
													getRoleNamesFromProject(
														sideContainerData,
														"reviewed_by"
													)
												)}
											</span>
										</div>
									</div>
									<div className="deliverable-card-form-item">
										<span className="deliverable-card-form-label">
											Description
										</span>
										<span className="form-item-content">
											{sideContainerData?.description || "-"}
										</span>
									</div>
									<div className="deliverable-card-form-item">
										<span className="deliverable-card-form-label">
											Document links
										</span>
										{sideContainerData.document_links &&
										sideContainerData?.document_links?.length > 0 ? (
											sideContainerData?.document_links.map((docLink: any) => {
												return (
													<span
														className="text-clickable form-item-content"
														onClick={() => {
															let url = docLink.link;
															if (!/^https?:\/\//i.test(url)) {
																url = "https://" + url;
															}
															window.open(url, "_blank");
														}}
													>
														{docLink.title}
													</span>
												);
											})
										) : (
											<span>-</span>
										)}
									</div>
									<div className="deliverable-card-form-item">
										<span className="deliverable-card-form-label">
											Attachment(s)
										</span>
										<span className="form-item-content">
											<DeliverablesSharepointComponent
												deliverableData={sideContainerData}
												taskData={taskInfo}
											/>

											{sideContainerData && (
												<ChecklistSection
													data={sideContainerData}
													updateData={(data: any) =>
														updateDeliverableAndAlertMessage(
															sideContainerData._id,
															data,
															sideContainerData
														)
													}
												/>
											)}

											<div
												style={{
													display: "flex",
													flexDirection: "column",
													gap: "16px",
												}}
											>
												{/* Discussion and Audit log buttons container */}
												<div style={{ display: "flex", gap: "16px" }}>
													<a style={{ marginTop: "auto" }}>
														<DeliverablesDiscussionModal
															title={sideContainerData?.deliverable_name}
															task={taskInfo}
															deliverable={sideContainerData}
														/>
													</a>

													<AuditLogButton
														moduleKey="project-delivered"
														handleClick={() => {
															setAuditDeliverableOpen(true);
														}}
													/>
												</div>
											</div>
										</span>
									</div>
								</Form>
							)}
						</div>
					</div>
					<div>
						{taskInfo?.closed !== true &&
							(sideContainerData.status === "New" ? (
								<Button
									className={
										!sideContainerData?.disabled
											? "deliverable-status-button mark-as-in-progress-button"
											: "deliverable-status-button deliverable-status-button-disabled"
									}
									onClick={() => {
										form.setFieldsValue({ status: "In progress" });
										form.submit();
									}}
									disabled={sideContainerData?.disabled}
									type="primary"
								>
									Mark as in progress
								</Button>
							) : sideContainerData.status === "In progress" ? (
								sideContainerData.reviewed_by &&
								sideContainerData.reviewed_by.length > 0 ? (
									<Popconfirm
										placement="topLeft"
										title={
											<div style={{ maxWidth: "300px", padding: "10px" }}>
												Submit for review? This will notify the responsible and
												accountable users for review and no further updates are
												allowed.
											</div>
										}
										overlayClassName="popconfirm-danger"
										icon={
											<ExclamationCircleOutlined style={{ color: "grey" }} />
										}
										onConfirm={() => {
											form.setFieldsValue({ status: "In review" });
											form.submit();
										}}
									>
										<Button
											className={
												!sideContainerData?.disabled
													? "deliverable-status-button submit-for-review-button"
													: "deliverable-status-button deliverable-status-button-disabled"
											}
											disabled={sideContainerData?.disabled}
											type="primary"
										>
											Submit for review
										</Button>
									</Popconfirm>
								) : checkPrivilege(props.params.userRole, ["tenant-admin"]) ||
								  project?.project_manager?.some(
										(pm: any) =>
											pm.toLowerCase() ===
											context.userInfo.user.email.toLowerCase()
								  ) ? (
									<Button
										disabled={sideContainerData?.disabled}
										className={
											!sideContainerData?.disabled
												? "deliverable-status-button mark-as-complete-button"
												: "deliverable-status-button deliverable-status-button-disabled"
										}
										onClick={() => {
											form.setFieldsValue({ status: "Resolved" });
											form.submit();
										}}
										type="primary"
									>
										Mark as complete
									</Button>
								) : (
									<Button
										className="deliverable-status-button submitted-for-review-button"
										type="primary"
										disabled
									>
										Submitted for review
									</Button>
								)
							) : (
								sideContainerData.status === "In review" &&
								(checkPrivilege(props.params.userRole, ["tenant-admin"]) ||
								project?.project_manager?.some(
									(pm: any) =>
										pm.toLowerCase() ===
										context.userInfo.user.email.toLowerCase()
								) ? (
									<Button
										disabled={sideContainerData?.disabled}
										className={
											!sideContainerData?.disabled
												? "deliverable-status-button mark-as-complete-button"
												: "deliverable-status-button deliverable-status-button-disabled"
										}
										onClick={() => {
											form.setFieldsValue({ status: "Resolved" });
											form.submit();
										}}
										type="primary"
									>
										Mark as complete
									</Button>
								) : (
									<Button
										className="deliverable-status-button submitted-for-review-button"
										type="primary"
										disabled
									>
										Submitted for review
									</Button>
								))
							))}
					</div>
				</div>
			);
		};

		return (
			<div
				style={{
					flex: "1",
					display: "flex",
					flexDirection: "row",
					gap: "20px",
					maxHeight: "100%",
					minHeight: "100%",
					overflow: "hidden",
				}}
			>
				<div
					style={{
						width: "60%",
						borderRadius: "3px",
						overflow: "hidden",
					}}
					ref={tableRef}
				>
					<Table
						rootClassName="deliverables-table"
						columns={deliverablesColumn}
						dataSource={(() => {
							const deliverableData =
								filteredDeliverableList || deliverableList;
							const shouldAddEmptyRow =
								deliverableData?.length > 0 &&
								tableDimensions.height > 0 &&
								deliverableData.length * 66 < tableDimensions.height;
							return shouldAddEmptyRow
								? [
										...deliverableData,
										{
											key: "empty-row",
											isEmpty: true,
										},
									]
								: deliverableData;
						})()}
						pagination={false}
						bordered
						onRow={(record) => {
							const deliverableData =
								filteredDeliverableList || deliverableList;
							return {
								className: record.isEmpty ? "deliverable-empty-row" : undefined,
								style: record.isEmpty
									? {
											height:
												tableDimensions.height - deliverableData.length * 64,
											backgroundColor: "transparent",
											border: "none",
										}
									: undefined,
							};
						}}
						scroll={{
							y: tableDimensions.height ? tableDimensions.height - 55 : 400,
							x: tableDimensions.width,
						}}
					/>
				</div>
				{sideContainerOpen && sideContainer()}
			</div>
		);
	};

	const handleCloseTask = (task: any) => {
		getCrudaClass("task").then((cruda: any) => {
			const initialData = { ...task };
			task.closed = true;
			task.reopen_reason = "";
			cruda
				.updateLookupItem(
					"tasks",
					null,
					task._id,
					context?.currentTenantKey,
					task,
					initialData,
					null,
					props.params.user,
					["closed"]
				)
				.then(() => {
					console.log("update successful");
					loadData(
						project_code,
						operationCode,
						phase_id,
						task_id,
						deliverableKey,
						context.allData
					);
					Emitter.emit("alert", {
						type: "success",
						message: `Task has been successfully closed.`,
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				})
				.catch(() => {
					Emitter.emit("alert", {
						type: "error",
						message: `Fail to close task.`,
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				});
		});
	};

	const handleReopenTask = (task: any) => {
		getCrudaClass("task").then((cruda: any) => {
			const initialData = { ...task };
			task.closed = false;
			cruda
				.updateLookupItem(
					"tasks",
					null,
					task._id,
					context?.currentTenantKey,
					task,
					initialData,
					null,
					props.params.user,
					["closed"]
				)
				.then(() => {
					console.log("update successful");
					loadData(
						project_code,
						operationCode,
						phase_id,
						task_id,
						deliverableKey,
						context.allData
					);
					Emitter.emit("alert", {
						type: "success",
						message: `Task is successfully reopened.`,
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				})
				.catch(() => {
					Emitter.emit("alert", {
						type: "error",
						message: `Fail to open task.`,
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				});
			setReopenModal(false);
		});
	};

	const resetForm = (values: any = null, reset: any = true) => {
		if (reopenForm) {
			setFormReset(true);
			reset && reopenForm.resetFields();
			reopenForm.setFieldsValue(values ? { ...values } : null);
		}
	};

	const handleFormCancel = () => {
		setReopenModal(false);
		setCancelPopover(false);
		resetForm();
	};

	const handleFormSave = async (values: any) => {
		// update values information
		values._id = taskInfo._id;
		values.closed = true;

		handleReopenTask(values);
		setReopenModal(false);
		resetForm();
		context.handleFormUpdating(false);
	};

	// console.log(
	// 	"taskInfo: ",
	// 	taskInfo,
	// 	"project: ",
	// 	project,
	// 	"operationCode: ",
	// 	operationCode
	// );

	return (
		<>
			{hasOperationalUpdatePermission.hasPermission ? (
				<>
					{taskInfo && (
						<div className="task-overview generic-container">
							{/* Top Container */}
							<div className="top-container main-container">
								<div
									className="container-header"
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<div
										className="panel-info"
										onClick={() => {
											setTaskDetailsExpand(!taskDetailsExpand);
										}}
									>
										{/* Task Details */}
										<span>Task details</span>
										{/* Dropdown Button Icon */}
										{taskDetailsExpand
											? GetAntIcon("up")
											: GetAntIcon("downarrow")}
									</div>
									{/* Panel Buttons Container */}
									<div
										className="panel-main-buttons-container"
										style={{ gap: "0" }}
									>
										<DeliverablesDiscussionModal
											title={taskInfo?.task_name}
											task={taskInfo}
										/>
										<Divider type="vertical" />
										{/* Task settings */}
										<TaskSettingsButton />
										<Divider type="vertical" />

										<AuditLogButton
											moduleKey="project-delivered"
											handleClick={() => {
												setAuditOpen(true);
											}}
										/>
									</div>
								</div>
								{/* Expanded Task details */}
								{taskDetailsExpand ? (
									<div className="details-content project-details-container">
										{renderMetaData()}
										<div className="task-details-content">
											{/* Hide the button for 'Closed' status and unrelated roles */}
											{(checkProjectTeam(
												props.params.user.email,
												responsibleParty
											) ||
												project?.project_manager?.some(
													(pm: any) =>
														pm.toLowerCase() ===
														props.params.user.email.toLowerCase()
												)) && (
												<>
													{taskInfo?.disabled !== true && (
														<>
															{taskInfo?.closed !== true ? (
																<Popconfirm
																	icon={
																		<ExclamationCircleOutlined
																			style={{ color: "grey" }}
																		/>
																	}
																	title={
																		<div
																			style={{
																				maxWidth: "300px",
																				padding: "10px",
																			}}
																		>
																			<>
																				Closing this task will update its status
																				to 'Closed.' You may still reopen the
																				task if needed. Do you wish to proceed?
																			</>
																		</div>
																	}
																	placement="topRight"
																	cancelButtonProps={{ ghost: true }}
																	onConfirm={() => handleCloseTask(taskInfo)}
																	okText="Close task"
																	okType="default"
																>
																	<Button type="primary">
																		{GetAntIcon("checkcircle")} Close task
																	</Button>
																</Popconfirm>
															) : (
																<Button
																	className="ant-btn-secondary"
																	onClick={() => setReopenModal(true)}
																>
																	{GetAntIcon("checkcircle")} Reopen task
																</Button>
															)}
														</>
													)}
												</>
											)}
										</div>
									</div>
								) : null}
								{reopenModal && (
									<Modal
										closable={false}
										width={"40%"}
										title="Reopen task?"
										open={reopenModal}
										centered
										destroyOnClose
										footer={() => {
											return (
												<div
													className="generic-footer"
													style={{
														height: "auto",
														padding: "0",
														width: "100%",
													}}
												>
													<Popconfirm
														icon={
															<ExclamationCircleOutlined
																style={{ color: "grey" }}
															/>
														}
														title={
															<div
																style={{ maxWidth: "300px", padding: "10px" }}
															>
																<>
																	You have unsaved changes. Discard them or
																	continue editing?
																</>
															</div>
														}
														open={cancelPopover}
														placement="topRight"
														cancelButtonProps={{ ghost: true }}
														cancelText="Discard changes"
														onCancel={() => {
															setCancelPopover(false);
															// handleFormCancel();
															setTimeout(() => {
																handleFormCancel();
															}, 300);
														}}
														okText="Continue editing"
														okType="default"
														onConfirm={() => {
															setCancelPopover(false);
														}}
													>
														<Button
															className="ant-btn-secondary"
															onClick={() => {
																if (formReset) {
																	handleFormCancel();
																} else {
																	setCancelPopover(true);
																}
															}}
														>
															Cancel
														</Button>
													</Popconfirm>
													<Popconfirm
														icon={
															<ExclamationCircleOutlined
																style={{ color: "grey" }}
															/>
														}
														title={
															<div
																style={{ maxWidth: "300px", padding: "10px" }}
															>
																<>
																	You have unsaved changes. Discard them or
																	continue editing?
																</>
															</div>
														}
														placement="topRight"
													>
														<FormButtonSave
															form={reopenForm}
															handleFormSave={handleFormSave}
															formReset={formReset}
															setFormReset={setFormReset}
														></FormButtonSave>
													</Popconfirm>
												</div>
											);
										}}
									>
										<CustomForm
											setFormReset={setFormReset}
											formRef={reopenForm}
											tabKey={"task-reopen"}
										/>
									</Modal>
								)}
							</div>
							{/* Bottom Container */}
							<div className="bottom-container main-container">
								{/* Bottom Container Header */}
								<div className="project-header">
									{/* Tab Buttons */}
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											gap: "10px",
										}}
									>
										{buttonTopic(
											{ key: "deliverables", label: "Deliverables" },
											"file2"
										)}
									</div>
								</div>
								<div className="generic-content project-overview-container">
									{/* Deliverables */}
									{activePage === "deliverables" && (
										<div className="kanban-columns-container">
											{deliverableList?.length > 0 ? (
												renderDeliverables()
											) : (
												<Empty
													className="no-data-empty"
													style={{ margin: "auto", fontSize: "20px" }}
													image={elementalSvg}
													description="Deliverables are not added yet."
												/>
											)}
										</div>
									)}
								</div>
							</div>
							<AuditLogModal
								tenantKey={context?.currentTenantKey}
								identifierKeys={{
									project_code: project_code,
									operation_code: operationCode,
									phase_id: phase_id,
									task_id: task_id,
								}}
								type={"task"}
								label={"Task"}
								open={auditOpen}
								handleClose={() => {
									setAuditOpen(false);
								}}
							/>

							<AuditLogModal
								tenantKey={context?.currentTenantKey}
								identifierKeys={{
									project_code: project_code,
									operation_code: operationCode,
									phase_id: phase_id,
									task_id: task_id,
									deliverable_id: sideContainerData?._id,
								}}
								type={"deliverable"}
								label={"Deliverable"}
								open={auditDeliverableOpen}
								handleClose={() => {
									setAuditDeliverableOpen(false);
								}}
							/>
						</div>
					)}
				</>
			) : (
				<NoAccess
					text={
						"Oops, looks like you don't have the authorisation to view this page."
					}
				/>
			)}
		</>
	);
}

export default TaskOverview;
